/* eslint-disable no-param-reassign */
/* eslint-disable import/extensions */
import { INSIGN_PHASE } from '@inface-sdk/web-game-auth'
import { PAGES } from '../../constants/GLOBAL_VARIABLE.js'
import { Feature } from '../common'
import { secondsToDhms } from '../../utils/time.js'

class Terms extends Feature {
  /**
   * @param {object} param
   * @param {import('../../services').GameAuthService} param.gameAuthService
   */
  constructor({
    mainUrl, securityUrl, gameAuthService, infaceAuth, useGameToken,
  }) {
    super({ namespace: 'iac', enumKey: 'IAC.TERMS' })

    this.mainUrl = mainUrl || 'https://signin.nexon.com'
    this.securityUrl = securityUrl || 'https://account-security.nexon.com'
    this.gameRestrictPath = '/security/gamerestrict/mobile'
    this.gameAuthService = gameAuthService
    this.infaceAuth = infaceAuth
    this.useGameToken = useGameToken ?? true

    this.$t = window.$i18next.t

    const {
      gid,
      guid,
      ticket,
      redirect_uri: redirectUri,
    } = this.queryParamWithCookie

    this.gid = gid
    this.guid = guid
    this.ticket = ticket
    this.redirectUri = redirectUri ? decodeURIComponent(redirectUri) : ''
  }

  setCookie(params = {}) {
    const { gid } = this.queryParamWithCookie
    const cookieData = {
      ...this.queryParamWithCookie, // 쿼리 파람으로 읽어온 값을 쿠키에 저장
      guid: this.guid,
      ticket: this.ticket,
      client_id: gid,
      signout_redirect_uri: encodeURIComponent(window.location.href),
      ...params,
    }

    this.namespaceCookie.setCookie(cookieData)
  }

  /**
   * goto insign-front /pages
   */
  gotoPages({ phase, path, params }) {
    this.setCookie(params)
    this.gameAuthService.setPhase(phase)
    this.navigateToUrlWithoutHistory(`${this.mainUrl}/${path}`)
  }

  redirect() {
    super.redirect(this.redirectUri)
  }

  async redirectWithTransaction() {
    if (this.useGameToken) {
      await this.gameAuthService.transactionByPhase({ guid: this.guid, ticket: this.ticket })
    }
    this.redirect()
  }

  /**
   * 게임탈퇴 복구 안내
   */
  async handleRecover({ days, hours, minutes }) {
    const action = await window.$globalModal.confirm({
      message: `${this.$t('recover_modal.message1', { D: days, HH: hours, mm: minutes })}\n${this.$t('recover_modal.message2')}`,
      confirmText: this.$t('recover_modal.confirm'),
    })

    if (action === 'confirm') { // 게임탈퇴 취소 처리 (복구)
      const { data } = await this.gameAuthService.issueIasTicketByWebToken() // wt -> ticket 발급
      await this.gameAuthService.recoverUser(data?.ticket)
    } else { // 취소 클릭 시 추가 안내
      await this.expireWebTokenIfGameTokenUnused()
      await window.$globalModal.warning({
        message: `${this.$t('recover_cancel_modal.message1')}\n${this.$t('recover_cancel_modal.message2')}`,
      })
      await this.showLogoutModalIfGameTokenUnused()

      if (this.infaceAuth?.platform?.type === 'arena') { // ARENA 플랫폼은 AToken 만료를 위해 로컬 로그아웃 페이지 진입
        await this.gotoLocalSignOutAndRedirect()
        return
      }
    }

    this.redirect()
  }

  /**
   * 휴면 해제 안내
   */
  async handleReactivate({
    message, memberId, memberType, npaCode,
  }) {
    const action = await window.$globalModal.confirm({
      message: this.$t('reactivate_modal.npres_reactivate_account_dialog_description') || message,
      confirmText: this.$t('reactivate_modal.npres_reactivate_account_dialog_positive_button_text'),
    })

    if (action === 'confirm') { // 휴면 해제 처리
      const { success, error } = await this.gameAuthService.insignGameAuth.reactivateAccountWithNpaCode({
        memberId, memberType, npaCode,
      })

      if (!success) {
        await this.handleErrorWithSignIn(error)
        return
      }

      await window.$globalModal.warning({
        message: this.$t('reactivate_modal.npres_reactivate_account_success_text'),
      })
    } else { // 휴면 해제 처리 취소 시
      await this.expireWebTokenIfGameTokenUnused()
      await this.showLogoutModalIfGameTokenUnused()

      if (this.infaceAuth?.platform?.type === 'arena') { // ARENA 플랫폼은 AToken 만료를 위해 로컬 로그아웃 페이지 진입
        await this.gotoLocalSignOutAndRedirect()
        return
      }
    }

    this.redirect()
  }

  /**
   * 본인인증 제한 해지 안내
   */
  async handleGameRestrict(message) {
    const action = await window.$globalModal.confirm({
      message,
      confirmText: this.$t('restrict_modal.confirm'),
    })

    if (action === 'confirm') { // 보안센터 게임실행제한 페이지 이동
      this.navigateToUrlWithoutHistory(`${this.securityUrl}${this.gameRestrictPath}?gameCode=${this.gid}`)
      return
    }

    // 본인인증 제한 해지 취소 시
    await this.expireWebTokenIfGameTokenUnused()
    await this.showLogoutModalIfGameTokenUnused()

    if (this.infaceAuth?.platform?.type === 'arena') { // ARENA 플랫폼은 AToken 만료를 위해 로컬 로그아웃 페이지 진입
      await this.gotoLocalSignOutAndRedirect()
      return
    }

    this.redirect()
  }

  async handleErrorWithSignIn(error = {}) {
    const { errorCode, errorText, result } = error.rawResponseData || {}

    let message = errorText || this.$t('error_modal.message')

    switch (errorCode) {
      case 1301: // 게임탈퇴 유예
        const { days, hours, minutes } = secondsToDhms(result.withdrawExpiresIn)

        if (this.gameAuthService.phase === INSIGN_PHASE.SA_AL_NX_LOGIN) { // PN 계정 복구 안내 페이지 이동
          this.gotoPages({ phase: INSIGN_PHASE.PN_AL_ERR_GP, path: PAGES.SA_SHOW_ACCOUNT_MENU_RESULT, params: { days, hours, minutes } })
          return
        }

        await this.handleRecover({ days, hours, minutes })
        return
      case 1202: // GUID 존재
        if (this.gameAuthService.phase === INSIGN_PHASE.SA_AL_NX_LOGIN) { // PN 계정 전환 안내 페이지 이동
          this.gotoPages({ phase: INSIGN_PHASE.PN_AL_ERR_GUID, path: PAGES.SA_SHOW_ACCOUNT_MENU_RESULT })
          return
        }

        break
      case 1205: // GUID 휴면
        const { memberId, memberType, npaCode } = result
        await this.handleReactivate({
          message: errorText, memberId, memberType, npaCode,
        })
        return
      case 1206: // NSRRS - 게임실행제한 (본인인증제한)
        await this.handleGameRestrict(errorText)
        return
      case -1:
      case -2:
      case 7300:
        message = this.$t('error_modal.message')
        break
      default:
    }

    if (errorCode) {
      message += `(${errorCode})`
    }

    await this.expireWebTokenIfGameTokenUnused()
    await window.$globalModal.warning({ message })
    await this.showLogoutModalIfGameTokenUnused()

    if (this.infaceAuth?.platform?.type === 'arena') { // ARENA 플랫폼은 AToken 만료를 위해 로컬 로그아웃 페이지 진입
      await this.gotoLocalSignOutAndRedirect()
      return
    }

    this.redirect()
  }

  async gotoLocalSignOutAndRedirect() {
    await this.infaceAuth.gotoSignOut({ redirect_uri: this.redirectUri })
  }

  async expireWebTokenIfGameTokenUnused() {
    if (!this.useGameToken) {
      await this.infaceAuth.expireWebToken()
    }
  }

  async showLogoutModalIfGameTokenUnused() {
    if (!this.useGameToken) {
      await window.$globalModal.warning({
        message: this.$t('logout_modal.message'),
      })
    }
  }

  async getTermsFlow(phase, params = {}) {
    const targetService = params.guid ? this.gameAuthService.insignGameAuth : this.infaceAuth.toyTermsService

    const { success, error, data } = await targetService.getTermsWithWebToken(
      null,
      params,
    )

    if (!success) {
      await this.handleErrorWithSignIn(error)
      return
    }

    if (this.gameAuthService.isAccountLinkPhase) {
      phase = INSIGN_PHASE.PN_AL_SUCCESS_TERMS_TK
      this.gameAuthService.processRemovePNSession() // 계정연동 성공 시 기존 게임토큰을 서버에서 날리기 때문에 쿠키에서 제거만.
    }

    const { guid, ticket } = data
    this.guid = guid
    this.ticket = ticket

    // 추가 약관 존재 시 IAC Flow
    if (data.termsAgree.length) {
      this.gotoPages({ phase, path: PAGES.IAC_CONSENT_TERMS })
      return
    }

    if (this.useGameToken) {
      const issueGTRes = await this.gameAuthService.insignGameAuth.issueGameToken(guid, ticket)
      await this.gameAuthService.handleResponse(issueGTRes)
    }

    await this.redirectWithTransaction()
  }

  async processLogin() {
    try {
      if (this.hasResponse()) {
        if (!this.hasError()) {
          window.$showLoginLoading()
        } else {
          await this.expireWebTokenIfGameTokenUnused()
          await this.showLogoutModalIfGameTokenUnused()

          if (this.infaceAuth?.platform?.type === 'arena') { // ARENA 플랫폼은 AToken 만료를 위해 로컬 로그아웃 페이지 진입
            await this.gotoLocalSignOutAndRedirect()
            return
          }
        }
        await this.redirectWithTransaction()
        return
      }

      await this.infaceAuth.init({ __useNppMonitor: false })

      // 비로그인 시 로그인 처리(로컬페이지 이동)
      if (!this.infaceAuth.isSignedIn()) {
        const { origin, pathname, search } = window.location
        const params = new URLSearchParams(search)
        params.delete('with_signout') // 로그인 후 돌아올 때는 해당 쿼리 제거 (무한 로그인)
        await this.infaceAuth.gotoSignIn({
          redirect_uri: `${origin}${pathname}?${params.toString()}`,
          // 콘솔에서 가져온 krpc_member_access_code 추가
          ...(this.infaceAuth.gidSetting.krpcMemberAccessCode && {
            options: {
              krpc: {
                memberAccessCode: this.infaceAuth.gidSetting.krpcMemberAccessCode,
              },
            },
          }),
        })
        return
      }

      if (this.gameAuthService.isAccountLinkPhase) {
        await this.getTermsFlow(this.gameAuthService.phase, { guid: await this.gameAuthService.insignGameAuth.guid })
      } else {
        await this.getTermsFlow(INSIGN_PHASE.GA_LOGIN_WT_TERMS)
      }
    } catch (e) {
      console.error('[processLogin]', e)
    }
  }

  async processConsentTerms() {
    try {
      if (this.hasResponse()) {
        await this.redirectWithTransaction()
        return
      }

      this.gotoPages({ path: PAGES.IAC_CONSENT_TERMS })
    } catch (e) {
      console.error('[processConsentTerms]', e)
    }
  }
}

export default Terms
